import { MouseEvent } from 'react';
import { useNavigate } from 'react-router-dom';

import { AppIcon, ICON_SIZES, PriorityPlaylist } from '@backrs_ui/common-ui';

import BackrsLogoSmallFilled from '@backrs_ui/common-ui/src/assets/images/backrs-logo-filled.svg';
import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';

import { Box, Button, SxProps, Theme, Typography } from '@mui/material';

import { PlaybookCardImage } from '../atoms/PlaybookCardImage';
import {
  SUGGESTED_PLAYLIST_CARD_IMAGE_HEIGHT,
  SUGGESTED_PLAYLIST_CARD_WIDTH,
} from '../constants/priorities.constants';
import { useAdoptSuggestedPlaybook } from '../hooks/playlists.hooks';
import { getPlaybookPath } from '../utils/playlist.utils';

const PLAYLIST_CARD_STRINGS = appStrings.modules.priorities.playlistCard;

type SuggestedPlaybookCardProps = Readonly<{
  playlist: PriorityPlaylist;
}>;

export const SuggestedPlaybookCard = ({ playlist }: SuggestedPlaybookCardProps) => {
  const navigate = useNavigate();

  const { adopt, isAdding } = useAdoptSuggestedPlaybook();

  const handleAddPlaybook = async (event: MouseEvent) => {
    event.stopPropagation();

    try {
      const usersPlaylist = await adopt(playlist);

      navigate(getPlaybookPath(usersPlaylist.id));
    } catch (error) {
      console.error('Failed to add suggested playlist:', error);
    }
  };

  return (
    <Box
      id={`suggested-playbook-card-${playlist.id}`}
      sx={cardSx}
      onClick={() => navigate(getPlaybookPath(playlist.id))}
    >
      <Box sx={imageAreaSx}>
        <PlaybookCardImage
          playlist={playlist}
          badgeIcon={<Box component="img" src={BackrsLogoSmallFilled} alt="" sx={logoSx} />}
        />
      </Box>

      <Box sx={contentSx}>
        <Typography variant="text-lg-semibold" sx={titleSx}>
          {playlist.title}
        </Typography>

        <Button
          id={`suggested-playbook-add-${playlist.id}`}
          hierarchy="Secondary gray"
          size="md"
          fullWidth
          disabled={isAdding}
          onClick={handleAddPlaybook}
          sx={addButtonSx}
          startIcon={<AppIcon iconName="Plus" diameter="20px" color="currentColor" />}
        >
          {PLAYLIST_CARD_STRINGS.addPlaylist}
        </Button>
      </Box>
    </Box>
  );
};

const cardSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  width: SUGGESTED_PLAYLIST_CARD_WIDTH,
  minWidth: SUGGESTED_PLAYLIST_CARD_WIDTH,
  flexShrink: 0,
  border: `1px solid ${backrs.colors['Colors/Base/Black']}`,
  borderRadius: backrs.radius['radius-xl'],
  overflow: 'hidden',
  backgroundColor: backrs.colors['Colors/Background/bg-primary'],
  cursor: 'pointer',
  userSelect: 'none',
  transition: 'box-shadow 0.3s ease-in-out, transform 0.3s ease-in-out',
  '&:hover': {
    boxShadow: backrs.shadows['shadow-lg'],
    transform: 'translate(-2px, -2px)',
  },
});

const imageAreaSx: SxProps<Theme> = ({ backrs }) => ({
  height: SUGGESTED_PLAYLIST_CARD_IMAGE_HEIGHT,
  flexShrink: 0,
  borderBottom: `1px solid ${backrs.colors['Colors/Base/Black']}`,
});

const contentSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  gap: backrs.spacing['spacing-2xl'],
  paddingX: backrs.spacing['spacing-xl'],
  paddingY: backrs.spacing['spacing-2xl'],
});

const titleSx: SxProps<Theme> = {
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  wordBreak: 'break-word',
};

const addButtonSx: SxProps<Theme> = {
  marginTop: 'auto',
};

const logoSx: SxProps<Theme> = ({ backrs }) => ({
  width: ICON_SIZES['16px'],
  height: ICON_SIZES['16px'],
  borderRadius: backrs.radius['radius-full'],
  objectFit: 'cover',
  flexShrink: 0,
});
